import * as React from 'react';
import { Button, Form, Offcanvas } from 'react-bootstrap';
import { SlidersHorizontal } from 'lucide-react';
import { EmploymentGradeOptions, EventTypeOptions, SpecialtyGroupOptions, YearGroupOptions } from 'cb-model';
import { ConditionElement, Filter } from 'gossamer-unified';
import styles from './WebsiteEventFilters.module.css';

export interface WebsiteEventFilterValues {
  locationType: string[];
  type: string[];
  specialty: string[];
  yearGroup: string[];
  employmentGrade: string[];
  dateFrom?: string;
  dateTo?: string;
}

export const emptyWebsiteEventFilterValues = (): WebsiteEventFilterValues => ({
  locationType: [],
  type: [],
  specialty: [],
  yearGroup: [],
  employmentGrade: [],
});

interface FilterOption {
  id: string;
  label: string;
}

interface FilterSection {
  key: keyof Pick<WebsiteEventFilterValues, 'locationType' | 'type' | 'specialty' | 'yearGroup' | 'employmentGrade'>;
  label: string;
  options: FilterOption[];
}

const sections: FilterSection[] = [
  {
    key: 'locationType',
    label: 'Location',
    options: [
      { id: 'in-person', label: 'In person event' },
      { id: 'online', label: 'Online event' },
      { id: 'hybrid', label: 'Hybrid event' },
    ],
  },
  {
    key: 'type',
    label: 'Type',
    options: EventTypeOptions.map(({ id, label }) => ({ id, label })),
  },
  {
    key: 'specialty',
    label: 'Specialty',
    options: SpecialtyGroupOptions.map(({ id, label }) => ({ id, label })),
  },
  {
    key: 'yearGroup',
    label: 'Year Group',
    options: YearGroupOptions.map(({ id, label }) => ({ id, label })),
  },
  {
    key: 'employmentGrade',
    label: 'Employment Grade',
    options: EmploymentGradeOptions.map(({ id, label }) => ({ id, label })),
  },
];

const selectedCount = (values: WebsiteEventFilterValues): number =>
  sections.reduce((count, section) => count + values[section.key].length, 0) +
  Number(Boolean(values.dateFrom)) +
  Number(Boolean(values.dateTo));

const toggleValue = (values: string[], value: string, checked: boolean): string[] =>
  checked ? [...values, value] : values.filter((candidate) => candidate !== value);

const addSelectedFilter = (filters: Filter[], values: string[], fieldId: string): void => {
  if (values.length > 0) {
    filters.push({ fieldId, operator: 'in', value: values });
  }
};

const getExclusiveDateToBoundary = (dateTo: string): string => {
  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateTo);
  if (!dateOnlyMatch) return dateTo;

  const [, year, month, day] = dateOnlyMatch;
  const nextDay = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day) + 1));
  return nextDay.toISOString();
};

export const makeWebsiteEventQueryFilters = (
  values: WebsiteEventFilterValues,
  options: {
    includeFutureDefault?: boolean;
    includeVisibilityFilters?: boolean;
    now?: Date;
    startFieldId?: string;
  } = {},
): Filter[] => {
  const startFieldId = options.startFieldId || 'start_at';
  const filters: Filter[] =
    options.includeVisibilityFilters === false
      ? []
      : [
          { fieldId: 'visibility_limit', operator: 'ne', value: 'closed' },
          { fieldId: 'visibility_limit', operator: 'ne', value: 'cancelled' },
        ];

  addSelectedFilter(filters, values.locationType, 'location_type');
  addSelectedFilter(filters, values.type, 'type');
  addSelectedFilter(filters, values.specialty, 'specialty_group');
  addSelectedFilter(filters, values.yearGroup, 'year_group');
  addSelectedFilter(filters, values.employmentGrade, 'employment_grade');

  if (values.dateFrom) {
    filters.push({ fieldId: startFieldId, operator: 'gt', value: values.dateFrom });
  }
  if (values.dateTo) {
    filters.push({
      fieldId: startFieldId,
      operator: 'lt',
      value: getExclusiveDateToBoundary(values.dateTo),
    });
  }
  if (!values.dateFrom && !values.dateTo && options.includeFutureDefault !== false) {
    filters.push({
      fieldId: startFieldId,
      operator: 'gt',
      value: (options.now || new Date()).toISOString(),
    });
  }

  return filters;
};

const eventFilterFieldIds = new Set([
  'location_type',
  'type',
  'specialty_group',
  'year_group',
  'employment_grade',
  'start_at',
  'visibility_limit',
]);

export const isFilter = (condition: ConditionElement): condition is Filter => 'fieldId' in condition;

export const removeEventFilters = (conditions: ConditionElement[] = []): ConditionElement[] =>
  conditions.filter((condition) => !isFilter(condition) || !eventFilterFieldIds.has(condition.fieldId));

export interface WebsiteEventFiltersProps {
  values: WebsiteEventFilterValues;
  onChange: (values: WebsiteEventFilterValues) => void;
  className?: string;
  heading?: string;
}

export const WebsiteEventFilterSidebar = ({
  className,
  ...filterProps
}: WebsiteEventFiltersProps): React.JSX.Element => {
  const [showFilters, setShowFilters] = React.useState(false);
  const id = React.useId();
  const count = selectedCount(filterProps.values);

  return (
    <div className={className}>
      <Button
        variant="outline-primary"
        className={styles.showFiltersButton}
        aria-controls={id}
        aria-expanded={showFilters}
        onClick={() => setShowFilters(true)}
      >
        <SlidersHorizontal size={18} aria-hidden="true" />
        Show filters{count > 0 ? ` (${count})` : ''}
      </Button>
      <Offcanvas
        id={id}
        className={styles.filterDrawer}
        responsive="lg"
        show={showFilters}
        onHide={() => setShowFilters(false)}
        aria-labelledby={`${id}-title`}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title id={`${id}-title`}>{filterProps.heading || 'Filters'}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className={styles.filterDrawerBody}>
          <WebsiteEventFilters {...filterProps} />
          <Button className={styles.doneButton} onClick={() => setShowFilters(false)}>
            Show results
          </Button>
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
};

export const WebsiteEventFilters = ({
  values,
  onChange,
  className,
  heading = 'Filters',
}: WebsiteEventFiltersProps): React.JSX.Element => {
  const count = selectedCount(values);

  const updateOptions = (section: FilterSection, option: FilterOption, checked: boolean) => {
    onChange({
      ...values,
      [section.key]: toggleValue(values[section.key], option.id, checked),
    });
  };

  return (
    <aside className={[styles.filters, className].filter(Boolean).join(' ')} aria-label="Event filters">
      <div className={styles.header}>
        <h2 className={styles.heading}>
          {heading}
          {count > 0 ? ` (${count})` : ''}
        </h2>
        <button
          type="button"
          className={styles.clearButton}
          disabled={count === 0}
          onClick={() => onChange(emptyWebsiteEventFilterValues())}
        >
          Clear
        </button>
      </div>

      <div>
        {sections.slice(0, 2).map((section) => (
          <details className={styles.section} key={section.key} open>
            <summary className={styles.summary}>{section.label}</summary>
            <div className={styles.options}>
              {section.options.map((option) => (
                <Form.Check
                  className={styles.check}
                  id={`event-filter-${section.key}-${option.id}`}
                  key={option.id}
                  type="checkbox"
                  label={option.label}
                  checked={values[section.key].includes(option.id)}
                  onChange={(event) => updateOptions(section, option, event.target.checked)}
                />
              ))}
            </div>
          </details>
        ))}

        <details className={styles.section} open>
          <summary className={styles.summary}>Date</summary>
          <div className={styles.dateGrid}>
            <div className={styles.dateField}>
              <label htmlFor="event-filter-date-from">From</label>
              <Form.Control
                id="event-filter-date-from"
                type="date"
                value={values.dateFrom || ''}
                onChange={(event) => onChange({ ...values, dateFrom: event.target.value || undefined })}
              />
            </div>
            <div className={styles.dateField}>
              <label htmlFor="event-filter-date-to">To</label>
              <Form.Control
                id="event-filter-date-to"
                type="date"
                min={values.dateFrom}
                value={values.dateTo || ''}
                onChange={(event) => onChange({ ...values, dateTo: event.target.value || undefined })}
              />
            </div>
          </div>
        </details>

        {sections.slice(2).map((section) => (
          <details className={styles.section} key={section.key} open>
            <summary className={styles.summary}>{section.label}</summary>
            <div className={styles.options}>
              {section.options.map((option) => (
                <Form.Check
                  className={styles.check}
                  id={`event-filter-${section.key}-${option.id}`}
                  key={option.id}
                  type="checkbox"
                  label={option.label}
                  checked={values[section.key].includes(option.id)}
                  onChange={(event) => updateOptions(section, option, event.target.checked)}
                />
              ))}
            </div>
          </details>
        ))}
      </div>
    </aside>
  );
};
